import { useSyncExternalStore } from "react";
import { UserSettings, defaultSettings, type UserSettings as Settings } from "@dfws/contracts";

// Phase 1 keeps settings in the browser. Phase 7 syncs them to the API (/api/v1/settings); the shape is already the contract.
const KEY = "dfws.settings";

function load(): Settings {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return defaultSettings;
    const parsed = UserSettings.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : defaultSettings;
  } catch {
    return defaultSettings;
  }
}

let state: Settings = typeof window === "undefined" ? defaultSettings : load();
const listeners = new Set<() => void>();

export function getSettings(): Settings {
  return state;
}

let remoteSave: ((s: Settings) => void) | null = null;
let saveTimer: ReturnType<typeof setTimeout> | undefined;

/** Connects the store to the account: server settings win on sign-in, later changes are saved back (debounced). */
export function connectRemote(remote: { stored: boolean; settings: unknown }, save: (s: Settings) => void): void {
  const parsed = UserSettings.safeParse(remote.settings);
  remoteSave = save;
  if (remote.stored && parsed.success) applyLocal(parsed.data);
  else save(state); // first sign-in on this account: keep what this browser already had
}

function applyLocal(next: Settings) {
  state = next;
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    /* private mode or blocked storage: keep in memory */
  }
  listeners.forEach((l) => l());
}

export function updateSettings(patch: Partial<Settings>): void {
  applyLocal({ ...state, ...patch });
  if (remoteSave) {
    clearTimeout(saveTimer);
    const save = remoteSave;
    saveTimer = setTimeout(() => save(state), 600);
  }
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useSettings(): Settings {
  return useSyncExternalStore(subscribe, getSettings, getSettings);
}

/** Test helper. */
export function resetSettings(): void {
  state = defaultSettings;
  listeners.forEach((l) => l());
}
